import { cap } from '../utils/format';
import React, { useState, useEffect } from 'react';
import { RefreshCw, Star, Clock, CheckCircle, TrendingUp, Award, AlertTriangle, Ticket, Users, X } from 'lucide-react';
import Badge from '../components/Badge';
import DateRangePicker from '../components/DateRangePicker';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid, Legend } from 'recharts';
import api from '../api/client';
import { PageHeader } from '../components/FilterBar';
import CompanyDeptFilter from '../components/CompanyDeptFilter';
import { useAuth } from '../context/AuthContext';

const DarkTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#0d1528', border: '1px solid rgba(59,130,246,0.4)', borderRadius: 10, padding: '10px 14px' }}>
      {label && <div style={{ color: '#64748b', fontSize: 11, marginBottom: 6 }}>{label}</div>}
      {payload.map((p, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: p.fill || p.stroke || p.color || '#60a5fa' }} />
          <span style={{ color: '#94a3b8', fontSize: 12 }}>{p.name}:</span>
          <span style={{ color: '#f1f5f9', fontSize: 12, fontWeight: 700 }}>{p.value}</span>
        </div>
      ))}
    </div>
  );
};

const RatingBadge = ({ rating }) => {
  if (!rating) return <span style={{ color: '#334155' }}>—</span>;
  const n = parseFloat(rating);
  const color = n >= 8 ? '#10b981' : n >= 6 ? '#f59e0b' : '#ef4444';
  return (
    <span className="flex items-center gap-1 font-bold" style={{ color }}>
      <Star size={11} fill={color}/> {n}/10
    </span>
  );
};

const StatCard = ({ label, value, icon: Icon, color, sub, onClick }) => (
  <div onClick={onClick} className={`card-3d glass glow-border rounded-2xl p-5 relative overflow-hidden group ${onClick ? 'cursor-pointer' : ''}`}
    style={onClick ? { transition: 'border-color 0.2s', borderColor: 'rgba(255,255,255,0.08)' } : {}}>
    <div className="absolute -top-4 -right-4 w-20 h-20 rounded-full pointer-events-none" style={{ background: color, opacity: 0.07, filter: 'blur(16px)' }}/>
    <div className="w-9 h-9 rounded-xl flex items-center justify-center mb-3" style={{ background: `${color}18`, border: `1px solid ${color}35` }}>
      <Icon size={16} style={{ color }}/>
    </div>
    <div className="text-2xl font-bold mb-0.5" style={{ color: '#f1f5f9' }}>{value ?? '—'}</div>
    <div className="text-xs font-medium" style={{ color: '#475569' }}>{label}</div>
    {sub && <div className="text-xs mt-1" style={{ color: '#334155' }}>{sub}</div>}
    {onClick && <div className="absolute bottom-2 right-3 text-xs opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: '#475569' }}>view details →</div>}
  </div>
);

const PERIODS = [
  { value: 'month', label: 'This Month' },
  { value: '3months', label: 'Last 3 Months' },
  { value: 'year', label: 'This Year' },
  { value: 'all', label: 'All Time' },
];

export default function KPIs() {
  const { isAdmin } = useAuth();
  const [data, setData] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('staff');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [filterCompany, setFilterCompany] = useState([]);
  const [filterDept, setFilterDept] = useState(null);
  const [drilldown, setDrilldown] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    const params = {};
    if (dateFrom) params.from = dateFrom;
    if (dateTo) params.to = dateTo;
    if (filterCompany.length) params.company_id = filterCompany.join(',');
    if (filterDept) params.department_id = filterDept;
    try {
      const [kpis, sats] = await Promise.all([
        api.get('/satisfaction/kpis', { params }).then(r => r.data),
        api.get('/satisfaction/results').then(r => r.data)
      ]);
      setData(kpis); setResults(sats);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [dateFrom, dateTo, filterCompany, filterDept]);

  const openDrilldown = async (title, type) => {
    setDrilldown({ title, type, tickets: [] });
    setDrillLoading(true);
    const params = { type };
    if (dateFrom) params.from = dateFrom;
    if (dateTo) params.to = dateTo;
    if (filterCompany.length) params.company_id = filterCompany.join(',');
    if (filterDept) params.department_id = filterDept;
    try {
      const r = await api.get('/satisfaction/kpi-tickets', { params });
      setDrilldown({ title, type, tickets: r.data || [] });
    } catch {} finally { setDrillLoading(false); }
  };

  const overall = data?.overall || {};
  const byStaff = data?.byStaff || [];
  const byCategory = data?.byCategory || [];
  const trend = data?.trend || [];

  const tabs = [
    ['staff', 'By IT Staff'],
    ['overview', 'Ticket Overview'],
    ['category', 'By Category'],
    ['trend', 'Monthly Trend'],
    ['surveys', 'Survey Results'],
  ];

  if (loading) return (
    <div className="flex items-center justify-center h-64" style={{ color: '#334155' }}>
      <div className="w-5 h-5 rounded-full border-2 border-blue-500 border-t-transparent animate-spin mr-3"/>
      Loading KPI data...
    </div>
  );

  return (
    <div className="space-y-4">
      <PageHeader title="IT Service KPIs">
        <div className="flex items-center gap-2 flex-wrap">
          {isAdmin && <CompanyDeptFilter companyId={filterCompany} deptId={filterDept} onChange={(c,d) => { setFilterCompany(c); setFilterDept(d); }}/>}
          <DateRangePicker from={dateFrom} to={dateTo} onChange={(f,t) => { setDateFrom(f); setDateTo(t); }}/>
          <button onClick={load} className="btn-ghost p-2"><RefreshCw size={13} className={loading?'animate-spin':''}/></button>
        </div>
      </PageHeader>

      {/* Top summary tiles — all clickable */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Total Tickets" value={overall.total_tickets} icon={Ticket} color="#818cf8"
          onClick={() => openDrilldown('All Tickets', 'total')}/>
        <StatCard label="Currently Open" value={overall.open_tickets} icon={AlertTriangle} color={parseInt(overall.open_tickets)>10?'#ef4444':'#f59e0b'}
          onClick={() => openDrilldown('Open Tickets', 'open')}/>
        <StatCard label="Resolved / Closed" value={`${overall.resolved_tickets || 0}`} icon={CheckCircle} color="#10b981"
          sub={overall.closed_tickets ? `${overall.closed_tickets} closed` : undefined}
          onClick={() => openDrilldown('Resolved & Closed Tickets', 'resolved')}/>
        <StatCard label="Avg Resolution Time" value={overall.avg_resolution_hours ? `${overall.avg_resolution_hours}h` : '—'} icon={Clock} color="#06b6d4"
          sub="resolved & closed tickets"/>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Surveys Sent" value={overall.total_surveys} icon={Star} color="#818cf8"
          onClick={() => openDrilldown('All Tickets (Survey Basis)', 'total')}/>
        <StatCard label="Responses Received" value={`${overall.responded || 0} / ${overall.total_surveys || 0}`} icon={CheckCircle} color="#10b981"
          onClick={() => openDrilldown('Resolved & Closed Tickets', 'resolved')}/>
        <StatCard label="Avg Satisfaction" value={overall.avg_rating ? `${overall.avg_rating}/10` : '—'} icon={Award} color={parseFloat(overall.avg_rating)>=8?'#10b981':parseFloat(overall.avg_rating)>=6?'#f59e0b':'#ef4444'}/>
        <StatCard label="SLA Breach (>24h open)" value={overall.sla_breach_count || 0} icon={AlertTriangle} color={parseInt(overall.sla_breach_count)>0?'#ef4444':'#10b981'}
          onClick={() => openDrilldown('SLA Breached Tickets (Open >24h)', 'sla')}/>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 rounded-xl w-fit flex-wrap" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}>
        {tabs.map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)}
            className="px-4 py-2 rounded-lg text-sm font-medium transition-all"
            style={{ background: tab===key?'rgba(59,130,246,0.2)':'transparent', color: tab===key?'#60a5fa':'#475569', border: tab===key?'1px solid rgba(59,130,246,0.3)':'1px solid transparent' }}>
            {label}
          </button>
        ))}
      </div>

      {/* ── BY IT STAFF ── */}
      {tab === 'staff' && (
        <div className="space-y-4">
          {byStaff.length === 0 ? (
            <div className="glass glow-border rounded-2xl p-10 text-center text-sm" style={{ color: '#1e3a5f' }}>
              No IT staff found. Add IT staff users under Settings.
            </div>
          ) : (
            <>
              {/* Chart */}
              <div className="glass glow-border rounded-2xl p-5">
                <div className="text-sm font-semibold mb-4" style={{ color: '#94a3b8' }}>Tickets Resolved vs Open by Staff</div>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart
                    data={byStaff.map(s => ({
                      name: s.staff_name?.split(' ')[0],
                      Resolved: parseInt(s.tickets_resolved) || 0,
                      Open: parseInt(s.tickets_open) || 0,
                      Rating: parseFloat(s.avg_rating) || 0,
                    }))}
                    margin={{ left: -10, right: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)"/>
                    <XAxis dataKey="name" tick={{ fill: '#1e3a5f', fontSize: 11 }}/>
                    <YAxis tick={{ fill: '#1e3a5f', fontSize: 11 }}/>
                    <Tooltip content={<DarkTooltip/>}/>
                    <Legend wrapperStyle={{ color: '#475569', fontSize: 11 }}/>
                    <Bar dataKey="Resolved" fill="#10b981" radius={[4,4,0,0]}/>
                    <Bar dataKey="Open" fill="#f59e0b" radius={[4,4,0,0]}/>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Staff table */}
              <div className="glass glow-border rounded-2xl overflow-x-auto">
                <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse', minWidth: 700 }}>
                  <thead>
                    <tr style={{ background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                      {['IT Staff Member','Role','Total','Resolved','Open','Surveys','Avg Rating','Avg Resolution'].map(h => (
                        <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: '#334155', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {byStaff.map((s, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}
                        onMouseEnter={e=>e.currentTarget.style.background='rgba(59,130,246,0.05)'}
                        onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                        <td style={{ padding: '11px 14px' }}>
                          <div style={{ color: '#e2e8f0', fontWeight: 600 }}>{s.staff_name}</div>
                        </td>
                        <td style={{ padding: '11px 14px' }}>
                          <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: s.role==='admin'?'rgba(239,68,68,0.15)':'rgba(59,130,246,0.15)', color: s.role==='admin'?'#f87171':'#60a5fa', border: `1px solid ${s.role==='admin'?'rgba(239,68,68,0.25)':'rgba(59,130,246,0.25)'}` }}>
                            {s.role}
                          </span>
                        </td>
                        <td style={{ padding: '11px 14px', color: '#94a3b8', fontWeight: 600 }}>{parseInt(s.total_tickets)||0}</td>
                        <td style={{ padding: '11px 14px' }}>
                          <span style={{ color: '#34d399', fontWeight: 700 }}>{parseInt(s.tickets_resolved)||0}</span>
                        </td>
                        <td style={{ padding: '11px 14px' }}>
                          <span style={{ color: parseInt(s.tickets_open)>0?'#f59e0b':'#475569', fontWeight: parseInt(s.tickets_open)>0?700:400 }}>{parseInt(s.tickets_open)||0}</span>
                        </td>
                        <td style={{ padding: '11px 14px', color: '#475569' }}>{parseInt(s.surveys_received)||0}</td>
                        <td style={{ padding: '11px 14px' }}><RatingBadge rating={s.avg_rating}/></td>
                        <td style={{ padding: '11px 14px', color: '#94a3b8' }}>
                          {s.avg_resolution_hours ? (
                            <span style={{ color: parseFloat(s.avg_resolution_hours) > 24 ? '#f87171' : parseFloat(s.avg_resolution_hours) > 8 ? '#f59e0b' : '#34d399' }}>
                              {s.avg_resolution_hours}h
                            </span>
                          ) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── TICKET OVERVIEW ── */}
      {tab === 'overview' && (
        <div className="space-y-4">
          <div className="glass glow-border rounded-2xl p-5">
            <div className="text-sm font-semibold mb-3" style={{ color: '#94a3b8' }}>KPI Targets vs Actual</div>
            <div className="space-y-3">
              {[
                { label: 'Avg Satisfaction Score', target: '≥ 8/10', actual: overall.avg_rating ? `${overall.avg_rating}/10` : '—', ok: parseFloat(overall.avg_rating) >= 8 },
                { label: 'Avg Resolution Time', target: '< 24h (routine)', actual: overall.avg_resolution_hours ? `${overall.avg_resolution_hours}h` : '—', ok: parseFloat(overall.avg_resolution_hours) < 24 },
                { label: 'Survey Response Rate', target: '≥ 50%', actual: overall.total_surveys > 0 ? `${Math.round((overall.responded/overall.total_surveys)*100)}%` : '—', ok: overall.total_surveys > 0 && (overall.responded/overall.total_surveys) >= 0.5 },
                { label: 'SLA Breaches (tickets open >24h)', target: '0', actual: overall.sla_breach_count || '0', ok: parseInt(overall.sla_breach_count) === 0 },
                { label: 'Happy Customer Rate (8+/10)', target: '≥ 80%', actual: overall.responded > 0 ? `${Math.round((overall.happy_count/overall.responded)*100)}%` : '—', ok: overall.responded > 0 && (overall.happy_count/overall.responded) >= 0.8 },
              ].map((r, i) => (
                <div key={i} className="flex items-center gap-4 p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}>
                  <div className="flex-1 text-sm font-medium" style={{ color: '#94a3b8' }}>{r.label}</div>
                  <div className="text-xs px-2 py-0.5 rounded" style={{ color: '#475569', background: 'rgba(255,255,255,0.04)' }}>Target: {r.target}</div>
                  <div className="font-bold text-sm min-w-[60px] text-right" style={{ color: r.actual === '—' ? '#334155' : r.ok ? '#34d399' : '#f87171' }}>{r.actual}</div>
                  <div className="text-xs font-medium" style={{ color: r.actual === '—' ? '#334155' : r.ok ? '#34d399' : '#f87171' }}>
                    {r.actual === '—' ? 'No data' : r.ok ? '✓ On target' : '✗ Below target'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── BY CATEGORY ── */}
      {tab === 'category' && (
        <div className="glass glow-border rounded-2xl overflow-x-auto">
          <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                {['Category','Total','Open','Resolved','Avg Satisfaction','Avg Resolution Time'].map(h => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: '#334155', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {byCategory.map((c, i) => (
                <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}
                  onMouseEnter={e=>e.currentTarget.style.background='rgba(59,130,246,0.05)'}
                  onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                  <td style={{ padding: '10px 14px', color: '#e2e8f0', fontWeight: 500, textTransform: 'capitalize' }}>{c.category?.replace(/_/g,' ').replace(/\b\w/g, l => l.toUpperCase())}</td>
                  <td style={{ padding: '10px 14px', color: '#94a3b8', fontWeight: 600 }}>{c.total}</td>
                  <td style={{ padding: '10px 14px', color: parseInt(c.open)>0?'#f59e0b':'#475569', fontWeight: parseInt(c.open)>0?700:400 }}>{c.open}</td>
                  <td style={{ padding: '10px 14px', color: '#34d399' }}>{c.resolved}</td>
                  <td style={{ padding: '10px 14px' }}><RatingBadge rating={c.avg_satisfaction}/></td>
                  <td style={{ padding: '10px 14px', color: '#94a3b8' }}>{c.avg_hours ? `${c.avg_hours}h` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── MONTHLY TREND ── */}
      {tab === 'trend' && (
        <div className="space-y-4">
          <div className="glass glow-border rounded-2xl p-5">
            <div className="text-sm font-semibold mb-4" style={{ color: '#94a3b8' }}>Tickets Created vs Resolved — Last 6 Months</div>
            {trend.length === 0 ? (
              <div className="h-48 flex items-center justify-center text-sm" style={{ color: '#1e3a5f' }}>No ticket history yet</div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={trend} margin={{ left: -10, right: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)"/>
                  <XAxis dataKey="month" tick={{ fill: '#1e3a5f', fontSize: 11 }}/>
                  <YAxis tick={{ fill: '#1e3a5f', fontSize: 11 }}/>
                  <Tooltip content={<DarkTooltip/>}/>
                  <Legend wrapperStyle={{ color: '#475569', fontSize: 11 }}/>
                  <Line type="monotone" dataKey="total" name="Total Tickets" stroke="#818cf8" strokeWidth={2} dot={{ fill: '#818cf8', r: 4 }}/>
                  <Line type="monotone" dataKey="resolved" name="Resolved" stroke="#10b981" strokeWidth={2} dot={{ fill: '#10b981', r: 4 }}/>
                  <Line type="monotone" dataKey="avg_rating" name="Avg Rating" stroke="#f59e0b" strokeWidth={2} strokeDasharray="4 4" dot={{ fill: '#f59e0b', r: 4 }}/>
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      )}

      {/* ── SURVEY RESULTS ── */}
      {tab === 'surveys' && (
        <div className="glass glow-border rounded-2xl overflow-x-auto">
          {results.length === 0 ? (
            <div className="flex items-center justify-center h-32 text-sm" style={{ color: '#1e3a5f' }}>
              No satisfaction surveys received yet. Sent automatically when tickets are resolved.
            </div>
          ) : (
            <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse', minWidth: 700 }}>
              <thead>
                <tr style={{ background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                  {['Ticket','Title','Reporter','IT Staff','Rating','Comments','Submitted'].map(h => (
                    <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: '#334155', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {results.map((r, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}
                    onMouseEnter={e=>e.currentTarget.style.background='rgba(59,130,246,0.05)'}
                    onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                    <td style={{ padding: '9px 12px', fontFamily: 'monospace', color: '#60a5fa' }}>{r.ticket_number}</td>
                    <td style={{ padding: '9px 12px', color: '#e2e8f0', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.title}</td>
                    <td style={{ padding: '9px 12px', color: '#94a3b8' }}>{r.reported_by || '—'}</td>
                    <td style={{ padding: '9px 12px', color: '#94a3b8' }}>{r.assigned_to_name || '—'}</td>
                    <td style={{ padding: '9px 12px' }}><RatingBadge rating={r.rating}/></td>
                    <td style={{ padding: '9px 12px', color: '#475569', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.remarks||'—'}</td>
                    <td style={{ padding: '9px 12px', color: '#334155', whiteSpace: 'nowrap' }}>{r.submitted_at ? new Date(r.submitted_at).toLocaleDateString('en-GB') : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── KPI Drilldown Popup ── */}
      {drilldown && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(4,8,16,0.88)', backdropFilter: 'blur(10px)' }}>
          <div className="relative w-full max-w-4xl max-h-[85vh] flex flex-col rounded-2xl overflow-hidden"
            style={{ background: 'linear-gradient(160deg,#0d1528,#090e1c)', border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 32px 80px rgba(0,0,0,0.85)' }}>
            <div className="flex items-center justify-between px-5 py-4 flex-shrink-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
              <div>
                <div className="font-bold text-sm" style={{ color: '#e2e8f0' }}>{drilldown.title}</div>
                <div className="text-xs mt-0.5" style={{ color: '#334155' }}>
                  {dateFrom||dateTo ? `${dateFrom||'start'} → ${dateTo||'now'}` : 'All Time'} · {drilldown.tickets?.length || 0} records
                </div>
              </div>
              <button onClick={() => setDrilldown(null)} className="btn-ghost p-1.5"><X size={15}/></button>
            </div>
            <div className="overflow-y-auto flex-1 p-2">
              {drillLoading ? (
                <div className="flex items-center justify-center h-32 gap-3" style={{ color: '#334155' }}>
                  <div className="w-4 h-4 rounded-full border-2 border-blue-500 border-t-transparent animate-spin"/>
                  Loading...
                </div>
              ) : drilldown.tickets?.length === 0 ? (
                <div className="flex items-center justify-center h-32" style={{ color: '#1e3a5f' }}>No tickets found for this filter</div>
              ) : (
                <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                      {['Ticket #','Title','Reporter','Assigned To','Priority','Status','Reported','Resolution Time'].map(h => (
                        <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontSize: 10, color: '#334155', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {drilldown.tickets.map((t, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}
                        onMouseEnter={e=>e.currentTarget.style.background='rgba(59,130,246,0.05)'}
                        onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                        <td style={{ padding: '8px 10px', fontFamily: 'monospace', color: '#60a5fa', whiteSpace: 'nowrap' }}>{t.ticket_number}</td>
                        <td style={{ padding: '8px 10px', color: '#e2e8f0', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.title}</td>
                        <td style={{ padding: '8px 10px', color: '#94a3b8' }}>{t.reporter||'—'}</td>
                        <td style={{ padding: '8px 10px', color: '#94a3b8' }}>{t.assigned_to||'—'}</td>
                        <td style={{ padding: '8px 10px' }}><Badge value={t.priority}/></td>
                        <td style={{ padding: '8px 10px' }}><Badge value={t.status}/></td>
                        <td style={{ padding: '8px 10px', color: '#475569', whiteSpace: 'nowrap' }}>{t.reported_date||'—'}</td>
                        <td style={{ padding: '8px 10px', color: parseFloat(t.hours_open)>24?'#f87171':'#94a3b8', whiteSpace: 'nowrap' }}>
                          {t.hours_open ? `${t.hours_open}h` : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
